import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { ErrorBoundary } from 'react-error-boundary';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { ThemeProvider } from '@emotion/react';
import { createTheme } from '@mui/material';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { AppTheme } from './theme';
import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import { LoadingScreen } from '@/components/loading';
import { Header } from '@/components/shared';
import { Footer } from '@/components/shared/footer';
import { Inquiry, InquiryFormPage } from '@/pages';
import { MaintenanceScreen, NotFound } from '@/screen';
import FormCompleteScreen from '@/screen/FormCompleteScreen';
import { useDefaultLanguage } from '@/utils/functions';
import { LANGUAGE_LIST, SUPPORTED_LANGUAGE_LIST } from '@/utils/languages/languageOptions';

function App() {
  const customMuiTheme = createTheme(AppTheme);

  const defaultLanguage = useDefaultLanguage();

  const locale =
    localStorage.getItem(LOCAL_LANGUAGE) !== null
      ? localStorage.getItem(LOCAL_LANGUAGE)
      : defaultLanguage;

  const lang = localStorage.getItem(LOCAL_LANGUAGE) || 'en';

  const baseUrl = lang !== null ? `/${lang}` : '';

  const messages = LANGUAGE_LIST.reduce((acc, { language, message }) => {
    acc[language] = message;
    return acc;
  }, {})[locale];

  const renderMultiRoutes = ({ element: Element, path, ...rest }) =>
    SUPPORTED_LANGUAGE_LIST.map((staticPath) => (
      <Route
        key={`${staticPath}/${path}`}
        path={`${staticPath}${path}`}
        {...rest}
        element={Element}
      />
    ));

  return (
    <IntlProvider locale={locale} messages={messages}>
      <ThemeProvider theme={customMuiTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={<MaintenanceScreen />}>
                <Suspense fallback={<LoadingScreen />}>
                  <Header />

                  <Routes>
                    <Route path="/" element={<Navigate to={`${baseUrl}/inquiry`} replace />} />

                    <Route
                      path={`${baseUrl}`}
                      element={<Navigate to={`${baseUrl}/inquiry`} replace />}
                    />

                    {renderMultiRoutes({ path: '/inquiry', element: <Inquiry /> })}

                    {renderMultiRoutes({ path: '/inquiry/:type', element: <InquiryFormPage /> })}

                    {renderMultiRoutes({ path: '/complete', element: <FormCompleteScreen /> })}

                    {renderMultiRoutes({ path: '/maintenance', element: <MaintenanceScreen /> })}

                    <Route path="/*" element={<NotFound />} />
                  </Routes>

                  <Footer />
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </LocalizationProvider>
      </ThemeProvider>
    </IntlProvider>
  );
}

export default App;
